import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Notification } from "../../../../../../@types/Notification";
import { IStaffTableData } from "../../../../../../@types/Staff";
import { Button } from "../../../../../../components/Button";
import { staffService } from "../../../../../../services/staffService";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import {
  CancelButton,
  ModalTitle,
} from "../../../SecurityModal/SecurityModal.styles";
import {
  ModalContainer,
  archiveButtonCss,
  SubmitContainer,
  ContentContainer,
  Row,
  Column,
} from "./ArchiveStaffModal.styles";

type ArchiveStaffModalProps = {
  staff: IStaffTableData;
  onClose: (notification?: Notification, refetchTable?: boolean) => void;
};

export function ArchiveStaffModal({ staff, onClose }: ArchiveStaffModalProps) {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isSubmittingRef = useRef(false);

  const handleArchiveStaff = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      setIsLoading(true);

      let message = "";
      let type: "success" | "info" | "warning" | "error" = "error";
      let refetchTable = false;

      if (staff.isActive) {
        mixpanelActions.track("User action: ArchiveStaffMember");

        const result = await staffService.archiveHospitlStaff(staff.id);

        if (result.status >= 200 && result.status < 300 && result.data) {
          message = t("settings_staff_archiveModal_archived");
          type = "success";
          refetchTable = true;
        } else {
          message = t("settings_staff_archiveModal_archiveFailed");
          type = "error";
        }
      } else {
        mixpanelActions.track("User action: ReactivateStaffMember");

        const result = await staffService.reactivateHospitlStaff(staff.id);

        if (result.status >= 200 && result.status < 300 && result.data) {
          message = t("settings_staff_archiveModal_reactivated");
          type = "success";
          refetchTable = true;
        } else {
          message = t("settings_staff_archiveModal_reactivateFailed");
          type = "error";
        }
      }

      onClose(
        {
          show: true,
          message,
          type,
          width: "max-content",
        },
        refetchTable
      );
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>
        {staff.isActive
          ? t("settings_staff_archiveModal_titleArchive")
          : t("settings_staff_archiveModal_titleReactivate")}
      </ModalTitle>

      <ContentContainer>
        <Row>
          <Column>
            <p style={{ fontWeight: "500" }}>
              {t("settings_staff_archiveModal_name")}
            </p>
            <p>{staff.name}</p>
          </Column>
          <Column>
            <p style={{ fontWeight: "500" }}>
              {t("settings_staff_archiveModal_email")}
            </p>
            <p>{staff.email}</p>
          </Column>
          <Column>
            <p style={{ fontWeight: "500" }}>
              {t("settings_staff_archiveModal_admin")}
            </p>
            <p>{t(staff.isAdmin ? "common_yes" : "common_no")}</p>
          </Column>
        </Row>
      </ContentContainer>

      {staff.isActive ? (
        <ContentContainer isNote>
          <Row>
            <Column isPrimaryColor>
              <p style={{ fontWeight: "500" }}>
                {t("settings_staff_archiveModal_noteTitle")}
              </p>
              <p>{t("settings_staff_archiveModal_noteBody")}</p>
            </Column>
          </Row>
        </ContentContainer>
      ) : null}

      <SubmitContainer>
        <Button
          label={
            staff.isActive
              ? t("settings_staff_archiveModal_archive")
              : t("settings_staff_archiveModal_reactivate")
          }
          type="button"
          onClick={handleArchiveStaff}
          isLoading={isLoading}
          className={archiveButtonCss()}
        />

        <CancelButton
          onClick={() => onClose()}
          style={{ marginLeft: "-35px", padding: "0 30px" }}
        >
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </ModalContainer>
  );
}
